import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, vShow as _vShow, createVNode as _createVNode, withDirectives as _withDirectives, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, normalizeStyle as _normalizeStyle, withCtx as _withCtx, normalizeClass as _normalizeClass, normalizeProps as _normalizeProps, guardReactiveProps as _guardReactiveProps, Teleport as _Teleport, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = ["dir"]
const _hoisted_2 = ["innerHTML"]

import { computed, markRaw, nextTick, onMounted, onUnmounted, ref, shallowReactive, shallowRef, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import i18n from 'i18next';

import L, { LatLngTuple, Layer } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.pattern';
import 'leaflet-easybutton/src/easy-button.css';
import 'leaflet-easybutton/src/easy-button.js';
import 'leaflet.polylinemeasure';
import 'leaflet.polylinemeasure/Leaflet.PolylineMeasure.css';

import hotkeys from 'hotkeys-js';
import * as indexDb from 'localforage';

import mapConfig from '@/map';

import { useRTL } from '@/composables/useRTL';

import { CountriesModel, DatesModel, EasyButtonControl, EventsModel, GeoModel, InfosModel, MapStructure, PlacesModel, TaggedMarker } from '@/models/sidebar/info.model';
import { PlaceCircle, RawPlace } from '@/models/sidebar/places.model';

import effects from '@/helpers/effects';
import parseGeoJson from '@/helpers/geoJson';
import iconMarker from '@/helpers/icons';
import { loadWarDatabase, mapCategoryByKey, mapCountryByKey } from '@/helpers/infoHelpers';
import markers from '@/helpers/marker';
import { cacheEvents, processCountryPopup } from '@/helpers/onEachFeature';
import panes from '@/helpers/panes';
import { cachePlaces, processPlaces } from '@/helpers/place';

import Search from '@/components/Search.vue';
import DateChanger from '@/components/DateChanger.vue';
import InfoCard from '@/components/Sidebar/InfoCard.vue';

import '@/assets/leaflet/L.Icon.FontAwesome.js';
import '@/assets/leaflet/L.Icon.FontAwesome.css';
import '@/assets/fonts/customIcon.css';

import '@/assets/css/map.css';
import '@/assets/css/pie.css';

const attribution = 'Map data (c) <a href="https://www.openstreetmap.org/">OpenStreetMap</a> & <a href="https://projectwaw.org">P:WaW</a> contributors, <a href="https://creativecommons.org/licenses/by-sa/2.0/">CC-BY-SA</a>, Imagery (c) <a href="https://www.mapbox.com/">Mapbox</a>';
const darkAttribution = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a> & <a href="https://projectwaw.org">P:WaW</a> contributors';


export default /*@__PURE__*/_defineComponent({
  __name: 'Map',
  setup(__props) {

// TODO: The file is too big, remove helper functions outside
const geosCache = shallowRef<GeoModel[]>([]);
const datesCache = shallowRef<DatesModel[]>([]);
const eventsCache = shallowRef<EventsModel[]>([]);
const countriesCache = shallowRef<CountriesModel[]>([]);
const placesCache = shallowRef<PlacesModel[]>([]);

const layer = shallowRef<Layer>();
const geolayer = shallowRef<Layer>();
const darkLayer = shallowRef<Layer>();

const placeInfoOverlay = ref();
const currentPlaceNotes = ref('');
const windowWidth = ref(window.innerWidth);
const selectedMarkerProps = ref<InfosModel | null>(null);

const popupTarget = shallowRef<HTMLElement | null>(null);

const markerGroup = L.layerGroup();
const placesGroup = L.layerGroup();
const crossesGroup = L.layerGroup();
const geoGroup = L.layerGroup();

const crossedOutIds = new Set<string>();

const effectPatterns: Record<string, L.StripePattern> = {};

let mapBounds = new L.LatLngBounds(new L.LatLng(0, 0), new L.LatLng(0, 0));
let mapCenter = new L.LatLng(0, 0);

const state = shallowReactive<MapStructure>({
  dbCountries: [],
  dbDates: [],
  dbEvents: [],
  dbGeos: [],
  dbSources: [],
  dbPlaces: [],
  dbCategories: [],
  bounds: new L.LatLngBounds(new L.LatLng(0, 0), new L.LatLng(0, 0)),
  center: new L.LatLng(0, 0),
  minZoom: 8,
  maxZoom: 14,
  effect: {},
  ready: false,
  previousWar: null,
});

const store = useStore();
const rtl = useRTL();
const { t, messages, locale } = useI18n();

const currentWar = computed(() => store.state.war);
const container = ref<HTMLDivElement | null>(null);

let openTooltip: L.Tooltip | null = null;
let placesMap: Record<string, PlaceCircle> = {};
let lightLabelsLayer: L.TileLayer | null = null;
let darkLabelsLayer: L.TileLayer | null = null;
let markerMap: Record<string, TaggedMarker> = {};

let customZoomControl: L.Control.Zoom | null = null;
let scaleControl: L.Control.Scale | null = null;
let measureControl: L.Control | null = null;
let placesBtn: EasyButtonControl | null = null;
let markersBtn: EasyButtonControl | null = null;
let mapBtn: EasyButtonControl | null = null;
let dateBtn: EasyButtonControl | null = null;

watch(() => store.state.date, () => {
  renderGeoJson();
  setupMarker();

  if (state.dbPlaces.length > 0) {
    if (Object.keys(placesMap).length > 0) {
      updatePlacesForDate();
    }
    else {
      setupPlaces();
    }
  }
});

watch(() => store.state.view, async () => {
  await nextTick();
  window.map.invalidateSize();
}, { immediate: true });

watch(locale, (newLocale, oldLocale) => {
  if (newLocale === oldLocale) {
    return;
  }

  // Closing active popups because figuring how to translate static html is too much of a hassle
  if (window.map) {
    window.map.closePopup();
  }
  if (typeof openTooltip !== 'undefined' && openTooltip) {
    openTooltip.remove();
    openTooltip = null;
  }

  store.commit('setLocale', newLocale);
  i18n.changeLanguage(newLocale);

  setupButtons();
  setLayout();
});

watch(currentWar, async (newWar, oldWar) => {
  if (newWar === oldWar) {
    return;
  }

  store.commit('changeConflict', newWar);

  geoGroup.clearLayers();
  markerGroup.clearLayers();
  placesGroup.clearLayers();
  crossesGroup.clearLayers();
  markerMap = {};
  placesMap = {};
  crossedOutIds.clear();

  if (newWar === 'suez-crisis') {
    store.commit('togglePlace', false);
  }
  else {
    store.commit('togglePlace', true);
  }

  await loadData();

  window.map.setMaxBounds(mapBounds);
  window.map.setView(mapCenter, state.minZoom);

  await setLayout();

  setupButtons();
});

watch(rtl, () => {
  setupButtons();
});

watch(windowWidth, (newWidth) => {
  if (newWidth >= 900 && window.map) {
    window.map.closePopup();
  }
});

watch(() => store.state.dark, () => {
  if (window.map) {
    window.map.closePopup();
  }

  if (typeof updateMapTiles === 'function') {
    updateMapTiles();
    updateLabels();
  }

  if (state.dbPlaces.length > 0 && Object.keys(placesMap).length > 0) {
    updatePlacesForDate();
  }
});

onMounted(async () => {
  locale.value = store.state.locale;

  await loadData();

  setupKeys();
  setupLeafletMap();
  setupPanes();
  setupLabels();
  setupPatterns();
  await setLayout();

  window.map.on('moveend', updateCrosses);

  setupButtons();
  store.commit('setReady', true);

  window.addEventListener('resize', updateWidth);
  window.openPlaceInfo = togglePlaceInfo;

  if (window.map) {
    window.map.on('movestart zoomstart', closePlaceInfo);
  }

  window.addEventListener('resize', updateWidth);

  // Some DOM manipulation to override a CSS bug that happened on the map
  const mapEl = document.getElementById('mapContainer');

  if (mapEl) {
    if (store.state.dark) {
      mapEl.classList.add('dark-mode');
    }

    watch(() => store.state.dark, (isDark) => {
      if (isDark) {
        mapEl.classList.add('dark-mode');
      }
      else {
        mapEl.classList.remove('dark-mode');
      }
    });
  }
});

const loadData = async () => {
  // TODO: db may be passed down from a parent component instead
  const db = await loadWarDatabase(currentWar.value);
  if (db) {
    state.dbCountries = markRaw(db.countries);
    state.dbDates = markRaw(db.dates);
    state.dbEvents = markRaw(db.events);
    state.dbGeos = markRaw(db.geo);
    state.dbSources = markRaw(db.sources);
    state.dbCategories = markRaw(db.categories);
    if (db.places) {

      state.dbPlaces = markRaw(db.places);
    }
    else {
      state.dbPlaces = [];
    }

    // TODO: Change this (obviously)
    switch (store.state.war) {
      case '1948-arab-israeli-war':
        mapBounds = new L.LatLngBounds(new L.LatLng(27.352253, 31.651611), new L.LatLng(34.316218, 42.451172));
        mapCenter = new L.LatLng(31.537979, 35.047868);
        break;
      case 'suez-crisis':
        mapBounds = new L.LatLngBounds(new L.LatLng(24.437147, 27.410888), new L.LatLng(35.764345, 37.287599));
        mapCenter = new L.LatLng(30.636602, 34.442586);
        break;
    }
  }
};

// Saving data in the cache for faster loading
const updateCache = async () => {
  const expiryTime = await indexDb.getItem<number>('expired');
  const expired = (expiryTime || 0) > Date.now();

  const rawGeos = await indexDb.getItem<string>('map');
  const rawDates = await indexDb.getItem<string>('dateRelation');
  const rawEvents = await indexDb.getItem<string>('events');
  const rawPlaces = await indexDb.getItem<string>('places');
  const rawCountries = await indexDb.getItem<string>('countries');

  geosCache.value = rawGeos ? JSON.parse(rawGeos) : [];
  datesCache.value = rawDates ? JSON.parse(rawDates) : [];
  eventsCache.value = rawEvents ? JSON.parse(rawEvents) : [];
  placesCache.value = rawPlaces ? JSON.parse(rawPlaces) : [];
  countriesCache.value = rawCountries ? JSON.parse(rawCountries) : [];

  if (!geosCache.value.length || expired || state.previousWar !== currentWar.value) {
    geosCache.value = state.dbGeos;
    await indexDb.setItem('map', JSON.stringify(geosCache.value));
    await indexDb.setItem('expired', Date.now() + 1000 * 60 * 60 * 24 * 7);
  }

  if (!datesCache.value.length || expired || state.previousWar !== currentWar.value) {
    datesCache.value = state.dbDates;
    await indexDb.setItem('dateRelation', JSON.stringify(datesCache.value));
  }

  if (!eventsCache.value.length || expired || state.previousWar !== currentWar.value) {
    eventsCache.value = state.dbEvents;
    await indexDb.setItem('events', JSON.stringify(eventsCache.value));
  }

  if (!placesCache.value.length || expired || state.previousWar !== currentWar.value) {
    placesCache.value = state.dbPlaces;
    await indexDb.setItem('places', JSON.stringify(placesCache.value));
  }

  if (!countriesCache.value.length || expired || state.previousWar !== currentWar.value) {
    countriesCache.value = state.dbCountries;
    await indexDb.setItem('countries', JSON.stringify(countriesCache.value));
  }
  state.previousWar = currentWar.value;
};

const updateWidth = () => {
  if (!container.value) {
    return;
  }
  windowWidth.value = window.innerWidth;
  container.value.style.marginTop = windowWidth.value <= 899 ? '10px' : '60px';
};

const setupLeafletMap = () => {
  window.map = L.map('mapContainer', {
    center: mapCenter,
    zoom: 8,
    maxBounds: mapBounds,
    maxBoundsViscosity: 1,
    zoomControl: false,
  });

  layer.value = L.tileLayer(mapConfig.normal, {
    attribution: attribution,
    minZoom: state.minZoom,
    maxZoom: state.maxZoom,
  });

  geolayer.value = L.tileLayer(mapConfig.satellite, {
    attribution: attribution,
    minZoom: state.minZoom,
    maxZoom: state.maxZoom,
  });

  darkLayer.value = L.tileLayer(mapConfig.dark, {
    attribution: darkAttribution,
    minZoom: state.minZoom,
    maxZoom: state.maxZoom,
  });

  updateMapTiles();
};

const updateMapTiles = () => {
  if (!window.map) {
    return;
  }

  if (layer.value) {
    window.map.removeLayer(layer.value);
  }
  if (darkLayer.value) {
    window.map.removeLayer(darkLayer.value);
  }
  if (geolayer.value) {
    window.map.removeLayer(geolayer.value);
  }

  if (store.state.geo) {
    if (geolayer.value) {
      window.map.addLayer(geolayer.value);
    }
  }
  else if (store.state.dark) {
    if (darkLayer.value) {
      window.map.addLayer(darkLayer.value);
    }
  }
  else if (layer.value) {
    window.map.addLayer(layer.value);
  }
};

const updateLabels = () => {
  if (lightLabelsLayer) {
    window.map.removeLayer(lightLabelsLayer);
  }
  if (darkLabelsLayer) {
    window.map.removeLayer(darkLabelsLayer);
  }

  if (store.state.dark) {
    if (darkLabelsLayer) {
      window.map.addLayer(darkLabelsLayer);
    }
  }
  else if (lightLabelsLayer) {
    window.map.addLayer(lightLabelsLayer);
  }
};

const setupLabels = () => {
  lightLabelsLayer = L.tileLayer(mapConfig.labels, {
    minZoom: state.minZoom,
    maxZoom: state.maxZoom,
    pane: 'labels',
  });

  darkLabelsLayer = L.tileLayer(mapConfig.darkLabels, {
    minZoom: state.minZoom,
    maxZoom: state.maxZoom,
    pane: 'labels',
  });

  updateLabels();
};

const renderGeoJson = () => {
  if (window.map.hasLayer(geoGroup)) {
    window.map.removeLayer(geoGroup);
  }

  geoGroup.clearLayers();

  const parsedRelation = parseGeoJson(geosCache.value, datesCache.value, store.state.date);
  for (const country of parsedRelation) {
    const isLine = country.properties.country.includes('commando');
    const geoJsonLayer = L.geoJSON(country, {
      style: {
        color: country.properties.color,
        fillPattern: effectPatterns ? effectPatterns[country.properties.effect] : null,
        weight: isLine ? 3 : 1,
      } as any,
      pane: country.properties.pane,
      onEachFeature: (feature, mapLayer) => {
        processCountryPopup(feature, mapLayer);
      },
    });

    geoJsonLayer.addTo(geoGroup);
  }

  window.map.addLayer(geoGroup);
};

const setupPanes = () => {
  for (const [key, value] of Object.entries(panes)) {
    window.map.createPane(key);
    const pane = window.map.getPane(key);
    if (pane) {
      pane.style.zIndex = value.toString();
    }
  }

  if (window.map) {
    window.map.createPane('crossesPane');
    const crossesPane = window.map.getPane('crossesPane');
    if (crossesPane) {
      crossesPane.style.zIndex = '450';
      crossesPane.style.pointerEvents = 'none';
    }
  }
};

const handlePlaceSelection = (place: RawPlace) => {
  if (window.map) {
    window.map.flyTo(place.location as L.LatLngExpression, 12, {
      animate: true,
      duration: 0.1
    });
  }

  const circle = placesMap[place._id];
  if (circle) {
    setTimeout(() => {
      circle.fire('click');
      circle.openPopup();
    }, 100);
  }
};

const setupPlacesButton = (pos: L.ControlPosition) => {
  const map = window.map;

  if (placesBtn) {
    const btnContainer = placesBtn.getContainer();
    if (btnContainer) {
      btnContainer.style.display = 'block';
    }
    if (placesBtn.options.position !== pos) {
      placesBtn.setPosition(pos);
    }
    if (placesBtn?.button) {
      placesBtn.button.title = t('mapcontrols.places');
    }
    return;
  }
  placesBtn = L.easyButton({
    id: 'toggle-places',
    position: pos,
    type: 'replace',
    leafletClasses: true,
    states: [
      {
        stateName: 'get-center',
        onClick: () => {
          store.commit('togglePlace', !store.state.place);
          if (store.state.place) {
            window.map.addLayer(placesGroup);
            window.map.addLayer(crossesGroup);
          }
          else {
            map.removeLayer(placesGroup);
            map.removeLayer(crossesGroup);
          }
        },
        title: t('mapcontrols.places'),
        icon: '<i class="fas fa-solid fa-city" style="font-size:16px;margin-left:-1px;"></i>',
      },
    ],
  }).addTo(map) as unknown as EasyButtonControl;
};

const setupButtons = () => {
  const map = window.map;
  const posControls: L.ControlPosition = rtl.value ? 'bottomright' : 'bottomleft';
  const posInfo: L.ControlPosition = rtl.value ? 'bottomleft' : 'bottomright';

  if (customZoomControl) {
    if (customZoomControl.options.position !== posControls) {
      customZoomControl.setPosition(posControls);
    }
  }
  else {
    customZoomControl = L.control.zoom({ position: posControls }).addTo(map);
  }

  if (scaleControl) {
    if (scaleControl.options.position !== posInfo) {
      scaleControl.setPosition(posInfo);
    }
  }
  else {
    scaleControl = L.control.scale({ position: posInfo }).addTo(map);
  }

  if (measureControl) {
    if (measureControl.options.position !== posControls) {
      measureControl.setPosition(posControls);
    }
  }
  else {
    measureControl = L.control.polylineMeasure({ position: posControls }).addTo(window.map) as unknown as L.Control;
  }
  if (measureControl) {
    const measureContainer = measureControl.getContainer();
    if (measureContainer) {
      const measureAnchor = measureContainer.querySelector('#polyline-measure-control') as HTMLAnchorElement;
      if (measureAnchor) {
        measureAnchor.title = t('mapcontrols.scale');
      }
    }
  }

  if (markersBtn) {
    if (markersBtn.options.position !== posControls) {
      markersBtn.setPosition(posControls);
    }
  }
  if (markersBtn?.button) {
    markersBtn.button.title = t('mapcontrols.markers');
  }
  else {
    markersBtn = L.easyButton({
      id: 'toggle-markers',
      position: posControls,
      type: 'replace',
      leafletClasses: true,
      states: [
        {
          stateName: 'get-center',
          onClick: () => {
            store.commit('toggleMarker');
            if (store.state.marker) {
              window.map.addLayer(markerGroup);
            }
            else {
              window.map.removeLayer(markerGroup);
            }
          },
          title: t('mapcontrols.markers'),
          icon: '<i class="fas fa-map-marker-alt" style="font-size:18px;"></i>',
        },
      ],
    }).addTo(window.map) as unknown as EasyButtonControl;
  }

  setupPlacesButton(posControls);
  if (currentWar.value === 'suez-crisis' || state.dbPlaces.length === 0) {
    if (placesBtn) {
      const btnContainer = placesBtn.getContainer();
      if (btnContainer) {
        btnContainer.style.display = 'none';
      }
    }
  }

  if (mapBtn) {
    if (mapBtn.options.position !== posControls) {
      mapBtn.setPosition(posControls);
    }
  }
  if (mapBtn?.button) {
    mapBtn.button.title = t('mapcontrols.map');
  }
  else {
    mapBtn = L.easyButton({
      id: 'change-map',
      position: posControls,
      type: 'replace',
      leafletClasses: true,
      states: [
        {
          stateName: 'get-center',
          onClick: () => {
            store.commit('toggleGeo');
            if (store.state.geo) {
              if (layer.value) {
                window.map.removeLayer(layer.value);
              }
              if (darkLayer.value) {
                window.map.removeLayer(darkLayer.value);
              }
              if (geolayer.value) {
                window.map.addLayer(geolayer.value);
              }
            }
            else {
              if (geolayer.value) {
                window.map.removeLayer(geolayer.value);
              }
              if (store.state.dark && darkLayer.value) {
                window.map.addLayer(darkLayer.value);
              }
              else if (layer.value) {
                window.map.addLayer(layer.value);
              }
            }
          },
          title: t('mapcontrols.map'),
          icon: '<i class="fas fa-satellite" style="font-size:18px;"></i>',
        },
      ],
    }).addTo(window.map) as unknown as EasyButtonControl;
  }

  if (dateBtn) {
    if (dateBtn.options.position !== posControls) {
      dateBtn.setPosition(posControls);
    }
  }
  if (dateBtn?.button) {
    dateBtn.button.title = t('mapcontrols.date');
  }
  else {
    dateBtn = L.easyButton({
      id: 'change-date',
      position: posControls,
      type: 'replace',
      leafletClasses: true,
      states: [
        {
          stateName: 'get-center',
          onClick: () => {
            store.commit('toggleDateChanger');
          },
          title: t('mapcontrols.date'),
          icon: '<i class="fas fa-calendar-week" style="font-size:18px;"></i>',
        },
      ],
    }).addTo(window.map) as unknown as EasyButtonControl;
  }
};

function resolveCategoryName(categoryId: string): string | null {
  const category = state.dbCategories.find(cat => cat._id === categoryId);
  if (!category) {
    return null;
  }
  return mapCategoryByKey(category, 'name', store.state.locale) as string;
}

const setupMarker = () => {
  const activeMarkers = markers(store.state.date, state.dbEvents);
  const activeIds = new Set(activeMarkers.map(m => m._id));

  for (const id in markerMap) {
    if (!activeIds.has(id)) {
      markerGroup.removeLayer(markerMap[id]);
    }
  }

  for (const marker of activeMarkers) {
    if (!markerMap[marker._id]) {
      const markerText = store.state.locale === 'en' ? marker.text : String(marker['text_' + store.state.locale]);

      const leafletMarker: TaggedMarker = new L.Marker((marker.location) as [number, number], {
        title: markerText,
        icon: L.icon.fontAwesome(iconMarker[marker.marker]),
      });

      leafletMarker.tag = 'marker';

      leafletMarker.on('click', () => {
        if (window.innerWidth < 900) {
          leafletMarker.unbindPopup();
          const anchorDiv = document.createElement('div');
          const fullEventRecord = state.dbEvents.find(e => e._id === marker._id);

          const eventCountry = state.dbCountries.find(
            (country) =>
              country._id === fullEventRecord?.country ||
              country.shortName === fullEventRecord?.country ||
              country['shortName_' + store.state.locale] === fullEventRecord?.country
          );

          const countryName = eventCountry ? (mapCountryByKey(eventCountry, 'shortName', store.state.locale) || mapCountryByKey(eventCountry, 'name', store.state.locale)) : '';

          selectedMarkerProps.value = {
            _id: marker._id,
            text: markerText,
            country: countryName,
            flag: eventCountry?.flag,
            source: marker.source,
            sources: state.dbSources
          };

          leafletMarker.bindPopup(anchorDiv, { minWidth: 280 }).openPopup();
          popupTarget.value = anchorDiv;
          return;
        }

        leafletMarker.unbindPopup();
        leafletMarker.closePopup();

        if (marker.category) {
          const categoryName = resolveCategoryName(marker.category);
          if (categoryName) {
            store.commit('setExpandCategory', categoryName);
          }
        }

        store.commit('setSidebar', 'info');
        store.commit('setView', 'sidebar');
        store.commit('setScrollTargetId', marker._id);
      });
      markerMap[marker._id] = leafletMarker;
      markerGroup.addLayer(leafletMarker);
    }
    else if (!markerGroup.hasLayer(markerMap[marker._id])) {
      markerGroup.addLayer(markerMap[marker._id]);
    }
  }
  if (store.state.marker && !window.map.hasLayer(markerGroup)) {
    window.map.addLayer(markerGroup);
  }
};

const updateCrosses = () => {
  if (!window.map || !store.state.place) {
    return;
  }

  crossesGroup.clearLayers();

  const zoom = window.map.getZoom();
  const currentWeight = Math.max(1, Math.min(4, zoom - 9));

  for (const placeId in placesMap) {
    const circle = placesMap[placeId];

    if (crossedOutIds.has(placeId)) {
      const centerLatLng = circle.getLatLng();
      const centerPoint = window.map.latLngToLayerPoint(centerLatLng);

      const edgeLatLng = L.latLng(centerLatLng.lat, circle.getBounds().getEast());
      const edgePoint = window.map.latLngToLayerPoint(edgeLatLng);
      const pixelRadius = Math.abs(edgePoint.x - centerPoint.x);

      const crossRadiusPixels = pixelRadius + 5;

      const nwPoint = L.point(centerPoint.x - crossRadiusPixels, centerPoint.y - crossRadiusPixels);
      const sePoint = L.point(centerPoint.x + crossRadiusPixels, centerPoint.y + crossRadiusPixels);
      const nePoint = L.point(centerPoint.x + crossRadiusPixels, centerPoint.y - crossRadiusPixels);
      const swPoint = L.point(centerPoint.x - crossRadiusPixels, centerPoint.y + crossRadiusPixels);

      const nw = window.map.layerPointToLatLng(nwPoint);
      const se = window.map.layerPointToLatLng(sePoint);
      const ne = window.map.layerPointToLatLng(nePoint);
      const sw = window.map.layerPointToLatLng(swPoint);

      const lineStyle = {
        color: '#e74c3c',
        weight: currentWeight,
        lineCap: 'round' as const,
        interactive: false,
        pane: 'crossesPane'
      };

      crossesGroup.addLayer(L.polyline([nw, se], lineStyle));
      crossesGroup.addLayer(L.polyline([sw, ne], lineStyle));
    }
  }

  if (!window.map.hasLayer(crossesGroup)) {
    window.map.addLayer(crossesGroup);
  }
};

const togglePlaceInfo = (event: MouseEvent, note: string) => {
  currentPlaceNotes.value = note;

  if (placeInfoOverlay.value) {
    placeInfoOverlay.value.toggle(event);
  }
};

const closePlaceInfo = () => {
  if (placeInfoOverlay.value) {
    placeInfoOverlay.value.hide();
  }
};

const setupPlaces = () => {
  placesMap = {};

  if (window.map.hasLayer(placesGroup)) {
    window.map.removeLayer(placesGroup);
  }
  if (window.map.hasLayer(crossesGroup)) {
    window.map.removeLayer(crossesGroup);
  }

  if (placesGroup) {
    placesGroup.clearLayers();
  }
  if (crossesGroup) {
    crossesGroup.clearLayers();
  }

  for (const place of processPlaces(placesCache.value, store.state.date)) {
    if (!place.id) {
      continue;
    }

    const sId = String(place.id);

    const circle = L.circle(place.location as LatLngTuple, {
      color: place.color,
      fillColor: place.color,
      fillOpacity: 0.2,
      radius: place.radius,
    }) as PlaceCircle;

    circle.bindPopup(place.popup, {
      minWidth: 320,
      maxWidth: 320
    });

    const tooltipOptions: L.TooltipOptions = {
      direction: 'top',
      className: 'place-hover-tooltip'
    };

    circle.bindTooltip(place.tooltip, tooltipOptions);
    circle.on('popupopen', () => {
      circle.unbindTooltip();
    });
    circle.on('popupclose', () => {
      circle.bindTooltip(place.tooltip, tooltipOptions);
    });

    placesMap[sId] = circle;
    placesGroup.addLayer(circle);

    if (place.isCrossedOut) {
      crossedOutIds.add(sId);
    }
    else {
      crossedOutIds.delete(sId);
    }
  }

  if (!store.state.place) {
    return placesMap;
  }

  window.map.addLayer(placesGroup);
  window.map.addLayer(crossesGroup);

  updateCrosses();

  return placesMap;
};

const updatePlacesForDate = () => {
  const processedPlaces = processPlaces(placesCache.value as RawPlace[], store.state.date);
  const activePlaceIds = new Set<string>();

  for (const place of processedPlaces) {
    if (!place.id) {
      continue;
    }

    const sId = String(place.id);
    activePlaceIds.add(sId);

    let circle = placesMap[sId];

    if (!circle) {
      circle = L.circle(place.location as LatLngTuple, {
        color: place.color,
        fillColor: place.color,
        fillOpacity: 0.2,
        radius: place.radius,
      }) as PlaceCircle;

      circle.bindPopup(place.popup, { minWidth: 320, maxWidth: 320 });
      circle.off('mouseover').off('mouseout');

      circle.on('click', () => {
        if (openTooltip) {
          openTooltip.remove();
          openTooltip = null;
        }
        const tooltip = circle.getTooltip();
        if (tooltip) {
          tooltip.addTo(window.map);
          openTooltip = tooltip;
        }
      });

      placesMap[sId] = circle;
      placesGroup.addLayer(circle);
    }
    else {
      if (circle.options.color !== place.color || circle.options.radius !== place.radius) {
        circle.setStyle({
          color: place.color,
          fillColor: place.color,
        });
        circle.setRadius(place.radius);
      }

      const popup = circle.getPopup();
      if (popup && popup.getContent() !== place.popup) {
        circle.setPopupContent(place.popup);
      }
    }

    if (place.isCrossedOut) {
      crossedOutIds.add(sId);
    }
    else {
      crossedOutIds.delete(sId);
    }
  }

  for (const id in placesMap) {
    if (!activePlaceIds.has(id)) {
      placesGroup.removeLayer(placesMap[id]);
      delete placesMap[id];
    }
  }

  updateCrosses();
};

const setupPatterns = () => {
  for (const effect of effects) {
    effectPatterns[effect.name] = new L.StripePattern(effect.style);
    effectPatterns[effect.name].addTo(window.map);
  }
};

const setupKeys = () => {
  hotkeys('ctrl+right', (event) => {
    event.preventDefault();
    store.commit('nextDate');
  });
  hotkeys('ctrl+left', (event) => {
    event.preventDefault();
    store.commit('backDate');
  });
};

const getLayout = () => {
  return rtl.value ? 'rtl' : 'ltr';
};

const setLayout = async () => {
  await updateCache();
  // refresh locale and layout
  cacheEvents(countriesCache.value, messages.value[locale.value]?.popupcountry || messages.value['en']?.popupcountry, store.state.locale, getLayout());
  cachePlaces(messages.value[locale.value]?.popupplace || messages.value['en']?.popupplace, getLayout());

  geoGroup.clearLayers();
  markerGroup.clearLayers();
  if (placesGroup) {
    placesGroup.clearLayers();
  }
  if (crossesGroup) {
    crossesGroup.clearLayers();
  }
  markerMap = {};
  placesMap = {};
  crossedOutIds.clear();

  renderGeoJson();
  setupMarker();
  state.dbPlaces.length > 0 && setupPlaces();
};

onUnmounted(() => {
  if (window.map) {
    window.map.remove();
  }
  window.removeEventListener('resize', updateWidth);
  hotkeys.unbind();
});

return (_ctx: any,_cache: any) => {
  const _component_OverlayPanel = _resolveComponent("OverlayPanel")!

  return (_openBlock(), _createElementBlock("div", {
    id: "container",
    dir: _unref(rtl) ? 'rtl' : 'ltr'
  }, [
    _createElementVNode("div", {
      class: _normalizeClass(["map-wrapper", {'sidebar-open': _unref(store).state.view === 'sidebar', 'dark-mode': _unref(store).state.dark}])
    }, [
      _withDirectives(_createVNode(Search, {
        places: _unref(state).dbPlaces,
        "is-rtl": _unref(rtl),
        onPlaceSelected: handlePlaceSelection
      }, null, 8, ["places", "is-rtl"]), [
        [_vShow, _unref(store).state.place]
      ]),
      _createVNode(_component_OverlayPanel, {
        ref_key: "placeInfoOverlay",
        ref: placeInfoOverlay,
        class: "no-arrow-overlay",
        appendTo: "body",
        dismissable: true,
        style: _normalizeStyle({
            maxWidth: '300px',
            backgroundColor: _unref(store).state.dark ? '#121722' : '#fff',
            color: _unref(store).state.dark ? '#E6EAF2' : '#000',
            fontSize: '14px',
            fontFamily: 'inherit',
            direction: _unref(rtl) ? 'rtl' : 'ltr',
            textAlign: _unref(rtl) ? 'right' : 'left',
            zIndex: 9999
          })
      }, {
        default: _withCtx(() => [
          _createElementVNode("div", { innerHTML: currentPlaceNotes.value }, null, 8, _hoisted_2)
        ]),
        _: 1
      }, 8, ["style"]),
      _cache[0] || (_cache[0] = _createElementVNode("div", {
        id: "mapContainer",
        class: "map",
        dir: "ltr"
      }, null, -1))
    ], 2),
    (popupTarget.value && selectedMarkerProps.value)
      ? (_openBlock(), _createBlock(_Teleport, {
          key: 0,
          to: popupTarget.value
        }, [
          _createVNode(InfoCard, _normalizeProps(_guardReactiveProps(selectedMarkerProps.value)), null, 16)
        ], 8, ["to"]))
      : _createCommentVNode("", true),
    _withDirectives(_createVNode(DateChanger, null, null, 512), [
      [_vShow, _unref(store).state.dateChanger]
    ])
  ], 8, _hoisted_1))
}
}

})